import { Alert, Button, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import ProductDetailMapDialog from "./ProductDetailMapDialog";
import type { BargainRow } from "./types";

const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "待提交",
    3: "已发布",
    4: "未通过",
    5: "已下架",
    6: "已关闭"
};
const bargainStateLabels: Record<number, string> = { 1: "进行中", 2: "已完成", 3: "已结束" };
const bargainWaitingPayLabels: Record<number, string> = {
    1: "30分钟",
    2: "60分钟",
    3: "12小时",
    4: "24小时",
    5: "48小时",
    6: "72小时"
};
const advertisingWatchLabels: Record<number, string> = { 1: "10秒", 2: "15秒", 3: "30秒", 4: "60秒" };
const payTypeLabels: Record<number, string> = { 1: "线上支付", 2: "线下支付", 3: "线上支付、线下支付" };

/** 将旧 Vue 详情的可选数字和单位保持为同一行文本。 */
function formatWithUnit(value: number | undefined, unit: string): string {
    return value == null ? "-" : `${value}${unit}`;
}

/** 保持旧 Vue 的提货周期展示，兼容接口只返回一端日期的情况。 */
function formatDeliveryPeriod(start?: string, end?: string): string {
    if (start && end) return `${start} 至 ${end}`;
    return start || end || "-";
}

/** 将旧 Vue 参数列表转换成 BaseItem 可直接渲染的文本。 */
function formatParameterInfo(parameterInfo?: BargainRow["productBargain"]["parameterInfo"]): string {
    return parameterInfo?.length
        ? parameterInfo.map(item => `${item.parameterName || "-"} ${item.parameterValue || "-"}`).join("、")
        : "-";
}

/** 将旧 Vue 标签数组转换成详情文本。 */
function formatTags(tags?: string[]): string {
    return tags?.length ? tags.join("、") : "-";
}

/** 将旧 Vue 品类及产区简称保持为列表相同的展示格式。 */
function formatProductType(productType?: BargainRow["productType"]): string {
    return productType?.name
        ? `${productType.name}${productType.areaAbbreviation ? ` (${productType.areaAbbreviation})` : ""}`
        : "-";
}

/** 沿用旧 Vue 详情头的状态组合顺序，避免状态枚举迁移后出现语义变化。 */
function getBargainStateDescription(product: BargainRow["productBargain"]): string {
    return (
        [
            productStateLabels[product.state || 0],
            publishStateLabels[product.publishState || 0],
            bargainStateLabels[product.bargainState || 0]
        ]
            .filter(Boolean)
            .join(" | ") || "-"
    );
}

export interface BargainDetailPageProps {
    auditBargain?: (id: number | string, payload: { state: boolean; auditDescn: string }) => Promise<void>;
    closeBargain?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfBargain?: (id: number | string) => Promise<void>;
    loadBargain?: (id: number | string) => Promise<BargainRow>;
    permissions?: string[];
}

/** 复用 Vue 捡漏商品详情接口。 */
export async function loadBargainDetail(id: number | string): Promise<BargainRow> {
    const response = await request.service.get<BargainRow>(`/admin/product-bargain/vo/${id}`);
    return response.data;
}

/** 复用 Vue 捡漏商品审核接口。 */
export async function auditBargain(
    id: number | string,
    payload: { state: boolean; auditDescn: string }
): Promise<void> {
    await request.service.put(`/admin/product-bargain/audit/${id}`, payload);
}

/** 复用 Vue 捡漏商品下架接口。 */
export async function downShelfBargain(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-bargain/down-shelf/${id}`);
}

/** 复用 Vue 捡漏商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeBargain(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-bargain/close/${id}`, { unActiveFileIds });
}

/** 将 Vue 捡漏详情的捡漏、商品和发布状态枚举统一为数字。 */
function normalizeBargainDetail(row: BargainRow): BargainRow {
    return {
        ...row,
        productBargain: {
            ...row.productBargain,
            bargainState: row.productBargain.bargainState == null ? undefined : Number(row.productBargain.bargainState),
            state: row.productBargain.state == null ? undefined : Number(row.productBargain.state),
            publishState: row.productBargain.publishState == null ? undefined : Number(row.productBargain.publishState)
        }
    };
}

/** 捡漏商品详情页，展示 Vue 详情页中的交易、提货、结算和服务字段。 */
export function BargainDetailPage({
    auditBargain: submitAudit = auditBargain,
    closeBargain: close = closeBargain,
    downShelfBargain: removeFromShelf = downShelfBargain,
    loadBargain = loadBargainDetail,
    permissions
}: BargainDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const id = new URLSearchParams(location.search).get("id");
    const isAuditPage = location.pathname.endsWith("/audit");
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [detail, setDetail] = useState<BargainRow>();
    const [product, setProduct] = useState<BargainRow["productBargain"]>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditState, setAuditState] = useState<boolean | null>(null);
    const [auditDescn, setAuditDescn] = useState("");
    const [auditSubmitting, setAuditSubmitting] = useState(false);

    useEffect(() => {
        if (!id) return;
        void loadBargain(id)
            .then(result => {
                const normalizedResult = normalizeBargainDetail(result);
                setErrorMessage(null);
                setDetail(normalizedResult);
                setProduct(normalizedResult.productBargain);
            })
            .catch(() => setErrorMessage("捡漏商品详情加载失败"));
    }, [id, loadBargain]);

    const refreshDetail = async () => {
        if (!id) return;
        const normalizedResult = normalizeBargainDetail(await loadBargain(id));
        setDetail(normalizedResult);
        setProduct(normalizedResult.productBargain);
    };

    const handleClose = async () => {
        if (!id || !detail) return;
        const unActiveFileIds = [
            detail.mainFile?.id,
            detail.videoFile?.id,
            ...(detail.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            navigate("/productManagement/bargain");
        } catch {
            // 关闭接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("捡漏商品关闭失败");
        }
    };

    const handleDownShelf = async () => {
        if (!id) return;
        try {
            await removeFromShelf(id);
            await refreshDetail();
        } catch {
            // 下架或刷新接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("捡漏商品下架失败");
        }
    };

    const handleAudit = async () => {
        if (!id || auditState == null) return;
        if (!auditState && !auditDescn.trim()) {
            setErrorMessage("请填写驳回原因");
            return;
        }
        setAuditSubmitting(true);
        try {
            await submitAudit(id, { state: auditState, auditDescn: auditDescn.trim() });
            navigate("/productManagement/bargain");
        } catch {
            setErrorMessage("捡漏商品审核失败");
        } finally {
            setAuditSubmitting(false);
        }
    };

    return (
        <section aria-label="bargain-detail-page">
            <div className="section-card">
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {product ? (
                    <>
                        <ZhDetailHeader
                            leftInfos={[
                                { label: "商品编号", value: product.id || "-" },
                                { label: "商品专员", value: product.handlerName || "-" }
                            ]}
                            stateDesc={getBargainStateDescription(product)}
                            title={isAuditPage ? "审核捡漏商品" : "捡漏商品详情"}
                        />
                        <ZhMultiGrid>
                            <ZhGrid className="mt-6" cols={4} gap={[32, 20]}>
                                <ZhGridItem span={4}>
                                    {/* 旧 Vue 详情使用 zh-base-item，保持标题和值的独立节点以复用详情字段契约。 */}
                                    <ZhBaseItem label="商品标题" value={product.title || "-"} />
                                </ZhGridItem>
                                <ZhBaseItem label="权重值" value={product.weight ?? "-"} />
                                <ZhBaseItem
                                    label="捡漏单价"
                                    value={formatWithUnit(product.bargainUnitPrice, "元/吨")}
                                />
                                <ZhBaseItem
                                    label="是否含税"
                                    value={
                                        product.isIncludeTax === 1
                                            ? "含税"
                                            : product.isIncludeTax === 0
                                              ? "不含税"
                                              : "-"
                                    }
                                />
                                <ZhBaseItem label="市场价" value={formatWithUnit(product.unitPrice, "元/吨")} />
                                <ZhBaseItem label="捡漏吨数" value={formatWithUnit(product.bargainTon, "吨")} />
                                <ZhBaseItem label="总价" value={formatWithUnit(product.totalCost, "元")} />
                                <ZhBaseItem label="捡漏开始时间" value={product.bargainStartTime || "-"} />
                                <ZhBaseItem label="活动天数" value={formatWithUnit(product.bargainCycle, "天")} />
                                <ZhBaseItem label="捡漏截止时间" value={product.bargainEndTime || "-"} />
                                <ZhBaseItem
                                    label="等待付款时间"
                                    value={bargainWaitingPayLabels[product.waitingPayTime || 0] || "-"}
                                />
                                <ZhBaseItem
                                    label="广告观看时间"
                                    value={advertisingWatchLabels[product.advertisingWatchTime || 0] || "-"}
                                />
                                <ZhBaseItem
                                    label="供应商"
                                    value={
                                        detail?.productBargain.supplierEnterprise?.enterpriseName ||
                                        detail?.productBargain.supplierEnterprise?.supplierName ||
                                        "-"
                                    }
                                />
                                <ZhBaseItem label="品类" value={formatProductType(detail?.productType)} />
                                <ZhBaseItem
                                    label="品类标签"
                                    value={formatTags(
                                        detail?.productTypeVo?.tags?.map(tag => tag.name).filter(Boolean) as string[]
                                    )}
                                />
                                <ZhBaseItem
                                    label="参数值"
                                    span={4}
                                    value={formatParameterInfo(product.parameterInfo)}
                                />
                                <ZhBaseItem label="商品标签" span={4} value={formatTags(product.productLabel)} />
                                <ProductDetailMapDialog location={product} />
                                <ZhBaseItem
                                    label="提货周期"
                                    value={formatDeliveryPeriod(product.deliveryStartTime, product.deliveryEndTime)}
                                />
                                <ZhBaseItem label="提货说明" span={4} value={product.deliveryDescn || "-"} />
                                <ZhBaseItem label="支付方式" value={payTypeLabels[product.payType || 0] || "-"} />
                                <ZhBaseItem label="结算说明" span={4} value={product.settlementDescn || "-"} />
                                <ZhBaseItem label="服务保障" span={4} value={product.serviceGuarantee || "-"} />
                                <ZhBaseItem label="捡漏规则" span={4} value={product.regulation || "-"} />
                                <ZhBaseItem
                                    label="商品专员联系电话"
                                    span={3}
                                    value={product.handlerMobile?.join("; ") || "-"}
                                />
                                <ZhBaseItem label="规格" value={product.productTypeSpec || "-"} />
                                <ZhBaseItem label="商品介绍" span={4} value={product.introduce || "-"} />
                                {product.publishState === 4 ? (
                                    <ZhBaseItem label="驳回原因" span={4} value={product.auditDescn || "-"} />
                                ) : null}
                            </ZhGrid>
                        </ZhMultiGrid>
                    </>
                ) : null}
                {isAuditPage && auditState != null ? (
                    <div className="mt-4">
                        <Input.TextArea
                            aria-label={auditState ? "审核意见" : "驳回原因"}
                            placeholder={auditState ? "请输入审核意见" : "请输入驳回原因"}
                            rows={4}
                            value={auditDescn}
                            onChange={event => setAuditDescn(event.target.value)}
                        />
                        <Button
                            className="mt-3"
                            aria-label="提交审核"
                            loading={auditSubmitting}
                            type="primary"
                            onClick={() => void handleAudit()}
                        >
                            提交审核
                        </Button>
                    </div>
                ) : null}
                <Space className="mt-4" wrap>
                    {isAuditPage ? (
                        <>
                            <Button aria-label="同意" onClick={() => setAuditState(true)}>
                                同意
                            </Button>
                            <Button aria-label="拒绝" onClick={() => setAuditState(false)}>
                                拒绝
                            </Button>
                        </>
                    ) : detail ? (
                        <>
                            {canManage && product?.publishState === 1 ? (
                                <Button
                                    aria-label="审核捡漏商品"
                                    onClick={() => void navigate(`/productManagement/bargain/audit?id=${id || ""}`)}
                                >
                                    审核
                                </Button>
                            ) : null}
                            {canDeal && product?.state === 2 && [1, 2, 4, 5].includes(product.publishState || 0) ? (
                                <Button
                                    aria-label="编辑捡漏商品"
                                    onClick={() => void navigate(`/productManagement/bargain/edit?id=${id || ""}`)}
                                >
                                    编辑
                                </Button>
                            ) : null}
                            {canClosePermission &&
                            product?.state === 2 &&
                            [1, 2, 4, 5].includes(product.publishState || 0) ? (
                                <Button aria-label="关闭捡漏商品" onClick={() => void handleClose()}>
                                    关闭
                                </Button>
                            ) : null}
                            {canDeal && product?.state === 1 && [3, 4].includes(product.publishState || 0) ? (
                                <Button aria-label="下架捡漏商品" onClick={() => void handleDownShelf()}>
                                    下架
                                </Button>
                            ) : null}
                            {canDeal &&
                            product?.state === 2 &&
                            product.publishState === 6 &&
                            product.bargainState === 2 ? (
                                <Button
                                    aria-label="复制捡漏商品"
                                    onClick={() => void navigate(`/productManagement/bargain/add?copyId=${id || ""}`)}
                                >
                                    复制
                                </Button>
                            ) : null}
                        </>
                    ) : null}
                    <Button onClick={() => void navigate("/productManagement/bargain")}>返回</Button>
                </Space>
            </div>
        </section>
    );
}

export default BargainDetailPage;
